/**
 * The TV title card's looks (producer 2026-10-16: "the motion graphics
 * titles are great, but some variation would be good"). A look is an
 * entrance, a backdrop and a colourway; each moment (a run starting, a game
 * starting, a win, something completed, a story beat) has a few of each to
 * choose from, so the same moment isn't identical twice but the cards still
 * read as one family: the same type, band and timing throughout.
 */

/** How the title arrives. */
export type TitleEntrance =
  /** From big, it slams down and the screen shakes. */
  | 'slam'
  /** It sweeps in from the left, leaning, and a shine runs over it. */
  | 'sweep'
  /** It stamps down, a little askew, in an inked frame, with a puff of dust. */
  | 'stamp'
  /** It bursts out of a flash, a ring of light racing out behind it. */
  | 'burst'
  /** Its letters drop in one by one and bounce. */
  | 'drop'
  /** A ribbon banner unfurls from the middle, and the title rises onto it. */
  | 'ribbon';

/** What plays behind it. */
export type TitleBackdrop =
  /** The dark band alone. */
  | 'band'
  /** Slowly turning rays behind the band. */
  | 'rays'
  /** The stage goes dark and a spotlight swings onto the title. */
  | 'spotlight'
  /** Confetti bursts as the title lands. */
  | 'confetti'
  /** Coins burst from behind the title as it lands. */
  | 'coins';

/** The card's colours. */
export type TitleColourway =
  /** Gold on near black: wins. */
  | 'gold'
  /** Warm wood with brass edges: games. */
  | 'wood'
  /** Night blue with ice-white type: the story. */
  | 'cool'
  /** Deep red with gold: a run starting, the showtime moment. */
  | 'ruby';

/** What the card is for; it picks the look. */
export type TitleKind = 'run' | 'game' | 'win' | 'complete' | 'story';

export interface TitleLook {
  entrance: TitleEntrance;
  backdrop: TitleBackdrop;
  colour: TitleColourway;
}

export const TITLE_ENTRANCES: readonly TitleEntrance[] = ['slam', 'sweep', 'stamp', 'burst', 'drop', 'ribbon'];
export const TITLE_BACKDROPS: readonly TitleBackdrop[] = ['band', 'rays', 'spotlight', 'confetti', 'coins'];
export const TITLE_COLOURWAYS: readonly TitleColourway[] = ['gold', 'wood', 'cool', 'ruby'];
export const TITLE_KINDS: readonly TitleKind[] = ['run', 'game', 'win', 'complete', 'story'];

/** Each moment's colourway, and the entrances and backdrops it draws from. */
export const TITLE_KIND_LOOKS: Readonly<
  Record<TitleKind, { colour: TitleColourway; entrances: readonly TitleEntrance[]; backdrops: readonly TitleBackdrop[] }>
> = {
  run: { colour: 'ruby', entrances: ['slam', 'burst', 'sweep'], backdrops: ['rays', 'spotlight'] },
  game: { colour: 'wood', entrances: ['sweep', 'stamp', 'drop', 'ribbon'], backdrops: ['band', 'spotlight'] },
  win: { colour: 'gold', entrances: ['burst', 'slam', 'drop'], backdrops: ['coins', 'rays'] },
  complete: { colour: 'gold', entrances: ['stamp', 'ribbon', 'burst'], backdrops: ['confetti', 'rays'] },
  story: { colour: 'cool', entrances: ['ribbon', 'drop', 'sweep'], backdrops: ['spotlight', 'band'] },
};

/** A card with no kind keeps the original look: it slams in on the band, in gold. */
export const CLASSIC_LOOK: TitleLook = { entrance: 'slam', backdrop: 'band', colour: 'gold' };

/**
 * Pick a look for `kind`: a random entrance and backdrop from the kind's
 * own, never the same entrance twice running for that kind (`last` is the
 * look it had last time). Anything in `fixed` is kept as given. `random`
 * returns a number in [0, 1).
 */
export function pickTitleLook(
  kind: TitleKind | undefined,
  options: { last?: TitleLook | null; fixed?: Partial<TitleLook>; random?: () => number } = {},
): TitleLook {
  const random = options.random ?? Math.random;
  const fixed = options.fixed ?? {};
  if (!kind) return { ...CLASSIC_LOOK, ...fixed };
  const looks = TITLE_KIND_LOOKS[kind];
  const pick = <T>(from: readonly T[]): T => from[Math.min(from.length - 1, Math.floor(random() * from.length))]!;
  const fresh = looks.entrances.filter((e) => e !== options.last?.entrance);
  return {
    entrance: fixed.entrance ?? pick(fresh.length > 0 ? fresh : looks.entrances),
    backdrop: fixed.backdrop ?? pick(looks.backdrops),
    colour: fixed.colour ?? looks.colour,
  };
}
